import { Injectable } from '@angular/core';
import { UserService } from '@app/core/services/user.service';
import { PcsTranslateService } from '@app/core/translate/translate.service';
import * as AdvancedSearchActions from '@app/pcs/actions/advanced-search';
import {
  AdvancedSearchActionTypes,
  FetchLabelsForUserFail,
  FetchLabelsForUserSuccess,
  SearchDossiersFail,
  UpdateSearchedPermanentAcceptance
} from '@app/pcs/actions/advanced-search';
import { setSearchResults } from '@app/pcs/actions/dossier-search';
import {
  DictionaryEntity,
  DossierSearchCriteria,
  DossierSearchType,
  isAnyCatalog,
  Label,
  ofSearchType,
  ProcessType,
  TrainType,
  userGridType,
  UserLabel
} from '@app/pcs/models';
import { PRODUCTION_ONLY } from '@app/pcs/models/dossier-search-agency-involvement';
import { UserGridType } from '@app/pcs/models/user-grid-type';
import * as fromAdvancedSearch from '@app/pcs/reducer/advanced-search';
import { AdvancedSearchRequiredFields, isRequiredField } from '@app/pcs/reducer/advanced-search/required-fields';
import {
  ADVANCED_SEARCH_AGENCY_INVOLVEMENT,
  ADVANCED_SEARCH_APPOINTED_AS_RU,
  ADVANCED_SEARCH_FORM_PROCESS_TYPE,
  ADVANCED_SEARCH_FORM_TRAIN_TYPE_ID,
  ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE,
  ADVANCED_SEARCH_MY_PROGRESS_STATUSES,
  AGENCY_INVOLVEMENT,
  MY_PROGRESS_STATUSES,
  TRAIN_TYPES
} from '@app/pcs/reducer/advanced-search/search-form';
import { AdvancedSearchDataService } from '@app/pcs/services/advanced-search-data.service';
import { DossierSearchGridService } from '@app/pcs/services/dossier-search-grid.service';
import { DossierStatusDataService } from '@app/pcs/services/dossier-status-data.service';
import { SearchCriteriaService } from '@app/pcs/services/search-criteria.service';
import { UserLabelDataService } from '@app/pcs/services/user-label-data.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { hasNoValue, hasValue, mapPayload } from '@util';
import { box, DisableAction, EnableAction, SetValueAction } from 'ngrx-forms';
import * as R from 'ramda';
import { asapScheduler, defer, iif, Observable, of, scheduled } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

type ToLabel = (userLabel: UserLabel) => Label;
const toLabel: ToLabel = R.prop('label');
export const processTypeControl = (controlId: string) => controlId.includes(ADVANCED_SEARCH_FORM_PROCESS_TYPE);

@Injectable()
export class AdvancedSearchEffects {

  private selectedDossierStatuses$ = this.store.pipe(select(fromAdvancedSearch.getSelectedDossierStatuses));
  private processType$ = this.store.pipe(select(fromAdvancedSearch.getSelectedProcessType));
  private agencyInvolvement$ = this.store.pipe(select(fromAdvancedSearch.getAgencyInvolvement));
  private searchType$ = this.store.pipe(select(fromAdvancedSearch.getSearchType));
  private imPermanentAcceptance$ = this.store.pipe(select(fromAdvancedSearch.getImPermanentAcceptance));

  fetchLabelsForUser$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.FetchLabelsForUser>(AdvancedSearchActionTypes.FetchLabelsForUser),
    exhaustMap(() =>
      this.userLabelDataService.fetchLabels().pipe(
        switchMap((userLabels: UserLabel[]) => [
          new FetchLabelsForUserSuccess(R.map(toLabel, userLabels))
        ]),
        catchError(err => of(new FetchLabelsForUserFail(err)))
      )
    )
  ));

  performAdvancedSearch$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.SearchDossiers>(AdvancedSearchActionTypes.SearchDossiers),
    mapPayload(),
    switchMap(({searchCriteria, dossierSearchType}) => {
      if (dossierSearchType !== DossierSearchType.FILTER) {
        this.searchCriteriaService.setSearchCriteria(searchCriteria, dossierSearchType);
      }

      const ttp = {id: searchCriteria.ttp.id};
      const gridType = userGridType(dossierSearchType);
      const newSearchCriteria = this.prepareSearchCriteria(dossierSearchType, searchCriteria);
      return hasValue(gridType)
        ? this.searchWithUserGridConfiguration({...newSearchCriteria, ttp}, gridType, dossierSearchType)
        : this.search({...newSearchCriteria, ttp}, dossierSearchType);
    })
  ));

  addRequiredFields$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.AddRequiredFields>(AdvancedSearchActionTypes.AddRequiredFields),
    mapPayload(),
    withLatestFrom(this.imPermanentAcceptance$),
    switchMap(([requiredFields, imPermanentAcceptance]: [AdvancedSearchRequiredFields, boolean]) =>
      defer(() => {
        if (isRequiredField(MY_PROGRESS_STATUSES, requiredFields)) {
          return this.resolveMyAcceptanceIndicator(imPermanentAcceptance);
        } else if (isRequiredField(TRAIN_TYPES, requiredFields)) {
          return this.resolveTrainType(requiredFields.trainTypes);
        } else if (isRequiredField(AGENCY_INVOLVEMENT, requiredFields)) {
          return [
            new DisableAction(ADVANCED_SEARCH_AGENCY_INVOLVEMENT),
            new DisableAction(ADVANCED_SEARCH_APPOINTED_AS_RU)
          ];
        } else {
          return scheduled([], asapScheduler);
        }
      })
    )
  ));

  fetchDossierStatuses$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.FetchDossierStatuses>(AdvancedSearchActionTypes.FetchDossierStatuses),
    mapPayload(),
    switchMap((processType) => this.dossierStatusDataService.fetchDossierStatusesForProcessType(processType).pipe(
      switchMap((dossierStatuses: DictionaryEntity[]) =>
        of(new AdvancedSearchActions.FetchDossierStatusesSuccess(dossierStatuses))),
      catchError((error: Error) => of(new AdvancedSearchActions.FetchDossierStatusesFail(error)))
    ))
  ));

  fetchDossierStatusesSuccess$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.FetchDossierStatusesSuccess>(AdvancedSearchActionTypes.FetchDossierStatusesSuccess),
    mapPayload(),
    map(R.sortBy(dossierStatus => this.translateService.translate(R.prop('description', dossierStatus)))),
    withLatestFrom(this.selectedDossierStatuses$),
    switchMap(([dossierStatuses, selectedDossierStatuses]: [DictionaryEntity[], DictionaryEntity[]]) =>
      this.initDossierStatuses(selectedDossierStatuses, dossierStatuses))
  ));

  checkBulkEditingProgress$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.CheckBulkEditingProgress>(AdvancedSearchActionTypes.CheckBulkEditingProgress),
    mapPayload(),
    exhaustMap((dossierSearchType: DossierSearchType) =>
      this.advancedSearchDataService.hasBulkEditingOperationInProgress(dossierSearchType).pipe(
        switchMap((hasOperationInProgress: boolean) => of(new AdvancedSearchActions.CheckBulkEditingProgressSuccess({hasOperationInProgress}))),
        catchError((error: Error) => of(new AdvancedSearchActions.CheckBulkEditingProgressFail(error)))
      )
    )
  ));

  fetchAgencyInvolvementOptions$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.FetchAgencyInvolvementOptions>(AdvancedSearchActionTypes.FetchAgencyInvolvementOptions),
    switchMap(() => this.advancedSearchDataService.fetchAgencyInvolvementOptions().pipe(
      switchMap((agencyInvolvementOptions: DictionaryEntity[]) =>
        of(new AdvancedSearchActions.FetchAgencyInvolvementOptionsSuccess(agencyInvolvementOptions))),
      catchError((error: Error) => of(new AdvancedSearchActions.FetchAgencyInvolvementOptionsFail(error)))
    ))
  ));

  disableAgencyInvolvementBasedOnProcessType$ = createEffect(() => this.actions$.pipe(
    ofType<SetValueAction<ProcessType>>(SetValueAction.TYPE),
    filter(({controlId}) => processTypeControl(controlId)),
    withLatestFrom(this.processType$, this.searchType$, this.agencyInvolvement$),
    switchMap(([_, processType, searchType, agencyInvolvement]) => searchType === DossierSearchType.BULK_RU_APPOINTMENT_PROCESSING
      ? scheduled([], asapScheduler)
      : this.handleAgencyInvolvement(processType, agencyInvolvement)
    )));

  disableAppointedAsRu$ = createEffect(() => this.actions$.pipe(
    ofType<SetValueAction<any>>(SetValueAction.TYPE),
    filter(({controlId}) => controlId.includes(ADVANCED_SEARCH_AGENCY_INVOLVEMENT)),
    withLatestFrom(this.agencyInvolvement$, this.searchType$),
    switchMap(([_, agencyInvolvement, searchType]) => searchType === DossierSearchType.BULK_RU_APPOINTMENT_PROCESSING
      ? scheduled([], asapScheduler)
      : this.handleAppointedRu(agencyInvolvement)
    )));

  disableAppointedAsRuOnInit$ = createEffect(() => this.actions$.pipe(
    ofType<AdvancedSearchActions.InitAdvancedSearchForm>(AdvancedSearchActionTypes.InitAdvancedSearchForm),
    withLatestFrom(this.agencyInvolvement$),
    switchMap(([_, agencyInvolvement]) => this.handleAppointedRu(agencyInvolvement))
  ));

  private handleAgencyInvolvement = (processType, agencyInvolvement) => iif(() => isAnyCatalog(processType),
    [
      ...this.resetAndDisableField(ADVANCED_SEARCH_AGENCY_INVOLVEMENT),
      ...this.resetAndDisableField(ADVANCED_SEARCH_APPOINTED_AS_RU),
    ],
    [
      new EnableAction(ADVANCED_SEARCH_AGENCY_INVOLVEMENT),
      ...this.handleAppointedRu(agencyInvolvement)
    ]);

  private handleAppointedRu = (agencyInvolvement) => agencyInvolvement?.id === PRODUCTION_ONLY.id
    ? [new EnableAction(ADVANCED_SEARCH_APPOINTED_AS_RU)]
    : [...this.resetAndDisableField(ADVANCED_SEARCH_APPOINTED_AS_RU)];

  private resetAndDisableField = (field) => [
    new SetValueAction(field, null),
    new DisableAction(field),
  ];

  private searchWithUserGridConfiguration = (searchCriteria: DossierSearchCriteria, userGridType: UserGridType, dossierSearchType: DossierSearchType) =>
    this.advancedSearchDataService.searchWithUserConfiguration(searchCriteria, userGridType).pipe(
      switchMap(({searchResults, userGridConfiguration}: any) =>
        dossierSearchType === DossierSearchType.BULK_PERMANENT_ACCEPTANCE
          ? [setSearchResults(searchResults, dossierSearchType, userGridConfiguration),
            new UpdateSearchedPermanentAcceptance(searchCriteria.imPermanentAcceptance)]
          : [setSearchResults(searchResults, dossierSearchType, userGridConfiguration)]),
      catchError((error: Error) => of(new SearchDossiersFail(error)))
    );

  private search = (searchCriteria: DossierSearchCriteria, dossierSearchType: DossierSearchType) =>
    this.advancedSearchDataService.search(searchCriteria).pipe(
      switchMap((searchResults: any) => dossierSearchType === DossierSearchType.BULK_PERMANENT_ACCEPTANCE
        ? [setSearchResults(searchResults, dossierSearchType, null),
          new UpdateSearchedPermanentAcceptance(searchCriteria.imPermanentAcceptance)]
        : [setSearchResults(searchResults, dossierSearchType, null)]),
      catchError((error: Error) => of(new SearchDossiersFail(error)))
    );

  private initDossierStatuses = (selectedDossierStatuses, dossierStatuses) => hasNoValue(selectedDossierStatuses)
  || R.all(dossierStatus => R.includes(dossierStatus, dossierStatuses), selectedDossierStatuses)
    ? [new AdvancedSearchActions.UpdateDossierStatuses(dossierStatuses)]
    : [new AdvancedSearchActions.UpdateDossierStatuses(dossierStatuses), new AdvancedSearchActions.ResetField('dossierStatuses')];

  private resolveMyAcceptanceIndicator(imPermanentAcceptance) {
    return [new DisableAction(ADVANCED_SEARCH_MY_PROGRESS_STATUSES),
      new EnableAction(ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE),
      new SetValueAction(ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE, imPermanentAcceptance)];
  }

  private resolveTrainType(trainTypes: TrainType[]): Observable<any> {
    return of(new SetValueAction(ADVANCED_SEARCH_FORM_TRAIN_TYPE_ID,
      box(R.head(trainTypes))));
  }

  private prepareSearchCriteria = (dossierSearchType, searchCriteria): DossierSearchCriteria => {
    const newSearchCriteria = R.omit(['trainType'], searchCriteria);
    if (!ofSearchType([DossierSearchType.CARRY_FORWARD, DossierSearchType.BULK_RU_APPOINTMENT])(dossierSearchType)) {
      return newSearchCriteria;
    }
    const trainTypes = R.hasIn('trainType') ? [R.prop('trainType', searchCriteria)] : [];
    return R.mergeDeepRight(newSearchCriteria, {trainTypes});
  };


  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private userService: UserService,
    private advancedSearchDataService: AdvancedSearchDataService,
    private userLabelDataService: UserLabelDataService,
    private searchCriteriaService: SearchCriteriaService,
    private dossierSearchGridService: DossierSearchGridService,
    private translateService: PcsTranslateService,
    private dossierStatusDataService: DossierStatusDataService) {
  }
}
